import { apiGet } from '../api/client';
import { getStoredLocale, isAppLocale, setAppLocale, type AppLocale } from '../i18n';
import { savePreferences, type PlayCurrency } from '../api/preferences';
import { getTelegramWebApp } from '../telegram/webapp';

/** Bump when first-visit geo rules change so older null-country defaults can re-apply once. */
const GEO_APPLIED_KEY = 'vbbplay.geo_defaults_applied_v2';
const CURRENCY_MANUAL_KEY = 'vbbplay.play_currency_manual';
const SUGGESTED_CURRENCY_KEY = 'vbbplay.suggested_play_currency';

interface GeoSuggest {
  country: string | null;
  locale: string;
  play_currency: string;
  source: string;
}

function hasManualCurrency(): boolean {
  try {
    return localStorage.getItem(CURRENCY_MANUAL_KEY) === '1';
  } catch {
    return false;
  }
}

function hasManualLocale(): boolean {
  try {
    return localStorage.getItem('vbbplay.locale_manual') === '1';
  } catch {
    return false;
  }
}

/** Telegram client language → app locale (az/ru/en/tr/…). */
export function telegramPreferredLocale(): AppLocale | null {
  try {
    const tg = getTelegramWebApp() as
      | { initDataUnsafe?: { user?: { language_code?: string } } }
      | null;
    const raw = (tg?.initDataUnsafe?.user?.language_code || '').trim().toLowerCase();
    if (!raw) return null;
    const primary = raw.split('-')[0];
    return isAppLocale(primary) ? primary : null;
  } catch {
    return null;
  }
}

/** Apply country currency + Telegram/browser locale once. Never overrides manual choices. */
export async function applyGeoDefaultsOnce(opts?: {
  loggedIn?: boolean;
  existingLocale?: string | null;
  existingPlayCurrency?: string | null;
}): Promise<void> {
  try {
    if (localStorage.getItem(GEO_APPLIED_KEY) === '1') return;
  } catch {
    // continue
  }

  const localeManual = hasManualLocale();
  const manualCurrency = hasManualCurrency();

  let suggest: GeoSuggest;
  try {
    suggest = await apiGet<GeoSuggest>('/api/v1/geo/suggest');
  } catch {
    return;
  }

  // Locale: Telegram language first, then geo soft suggestion. Manual always wins.
  let chosenLocale: AppLocale | null = null;
  if (!localeManual) {
    const tgLocale = telegramPreferredLocale();
    if (tgLocale) {
      chosenLocale = tgLocale;
    } else if (isAppLocale(suggest.locale)) {
      chosenLocale = suggest.locale as AppLocale;
    } else if (!getStoredLocale() && isAppLocale(suggest.locale)) {
      chosenLocale = suggest.locale as AppLocale;
    }
    if (chosenLocale) {
      setAppLocale(chosenLocale);
    }
  }

  const currency = suggest.play_currency as PlayCurrency;
  if (!manualCurrency && ['AZN', 'RUB', 'USD', 'EUR'].includes(currency)) {
    try {
      localStorage.setItem(SUGGESTED_CURRENCY_KEY, currency);
    } catch {
      // ignore
    }
  }

  if (opts?.loggedIn) {
    const body: { play_currency?: PlayCurrency; locale?: AppLocale } = {};
    if (!manualCurrency && ['AZN', 'RUB', 'USD', 'EUR'].includes(currency)) {
      body.play_currency = currency;
    }
    if (!localeManual && chosenLocale) {
      body.locale = chosenLocale;
    }
    if (Object.keys(body).length) {
      try {
        await savePreferences(body);
      } catch {
        // ignore preference write failures (e.g. open session)
      }
    }
  }

  try {
    localStorage.setItem(GEO_APPLIED_KEY, '1');
  } catch {
    // ignore
  }
}

export function markPlayCurrencyManual(): void {
  try {
    localStorage.setItem(CURRENCY_MANUAL_KEY, '1');
  } catch {
    // ignore
  }
}

/** After login: apply stored first-visit currency if user never chose manually. */
export async function applySuggestedCurrencyAfterLogin(): Promise<void> {
  if (hasManualCurrency()) return;
  let currency: string | null = null;
  try {
    currency = localStorage.getItem(SUGGESTED_CURRENCY_KEY);
  } catch {
    currency = null;
  }
  if (!currency || !['AZN', 'RUB', 'USD', 'EUR'].includes(currency)) return;
  try {
    await savePreferences({ play_currency: currency as PlayCurrency });
  } catch {
    // ignore
  }
}
